/**
 * Upload progress, expandable.
 *
 * Collapsed it is one line — the batch, a percentage, a bar. Tapping it opens
 * the per-file detail: which stage each file is at, how far along, and what
 * went wrong for the ones that failed, with retry and dismiss per row.
 *
 * The previous version showed a single aggregate number, so a batch where one
 * photo failed looked identical to one where all of them succeeded.
 */

import { useState } from "react";
import { useT } from "../../lib/i18n.js";
import { formatBytes } from "@fc/core";
import { Button } from "../../components/ui/Button.js";
import type { UploadStatus, UploadTask } from "./useUploadQueue.js";
import "./UploadPanel.css";

export interface UploadPanelProps {
  tasks: UploadTask[];
  active: UploadTask[];
  progress: number;
  completedCount: number;
  failedCount: number;
  totalBytes: number;
  uploadedBytes: number;
  settled: boolean;
  onRetry: (id: string) => void;
  onCancel: (id: string) => void;
  onDismiss: () => void;
}

/** Human wording per stage — "finalising" means nothing to a person. */
const STAGE_LABEL: Record<UploadStatus, string> = {
  queued: "Waiting",
  hashing: "Checking",
  // Not "Encrypting": the word invites the question of what happens to the
  // photographs that are not, and that answer belongs in the group's settings,
  // not on a progress row that is gone again in two seconds.
  sealing: "Preparing",
  uploading: "Uploading",
  finalising: "Finishing",
  processing: "Processing",
  done: "Added",
  duplicate: "Already here",
  failed: "Failed",
  cancelled: "Cancelled",
};

export function UploadPanel({
  tasks,
  active,
  progress,
  completedCount,
  failedCount,
  totalBytes,
  uploadedBytes,
  settled,
  onRetry,
  onCancel,
  onDismiss,
}: UploadPanelProps) {
  const t = useT();
  const [expanded, setExpanded] = useState(false);

  if (active.length === 0) return null;

  const state = failedCount > 0 ? "error" : settled ? "done" : "busy";
  const percent = Math.round(progress * 100);

  // Once every byte has landed the bar is pinned at 100% and the wording has to
  // carry the remaining work, or the panel looks frozen while the server
  // decodes a large batch.
  const processingCount = active.filter(
    (task) => task.status === "processing",
  ).length;

  return (
    <section
      className="fc-uploads"
      data-state={state}
      data-expanded={expanded || undefined}
      aria-label={t("Upload progress")}
    >
      <button
        type="button"
        className="fc-uploads__summary"
        aria-expanded={expanded}
        onClick={() => setExpanded((value) => !value)}
      >
        <span className="fc-uploads__title">
          {settled
            ? failedCount > 0
              ? // Not "didn't upload": a file that fails to decode uploaded
                // perfectly well, and telling the user otherwise sends them
                // off to check their connection for no reason.
                `${failedCount} of ${active.length} didn't finish`
              : `Added ${completedCount} ${completedCount === 1 ? "photo" : "photos"}`
            : processingCount > 0 && percent === 100
              ? `Processing ${processingCount} ${processingCount === 1 ? "photo" : "photos"}`
              : `Uploading ${completedCount} / ${active.length}`}
        </span>

        <span className="fc-uploads__percent fc-tabular">
          {settled || processingCount > 0 ? "" : `${percent}%`}
        </span>

        <svg
          className="fc-uploads__chevron"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      <div
        className="fc-uploads__track"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="fc-uploads__fill"
          style={{ transform: `scaleX(${progress})` }}
        />
      </div>

      {!expanded && (
        <p className="fc-uploads__hint">
          {settled
            ? failedCount > 0
              ? "Tap to see what went wrong."
              : "All done."
            : processingCount > 0 && percent === 100
              ? "Uploaded. Building previews — safe to keep browsing."
              : `${formatBytes(uploadedBytes)} of ${formatBytes(totalBytes)} · tap for detail`}
        </p>
      )}

      {expanded && (
        <ul className="fc-uploads__list">
          {tasks
            .filter((task) => task.status !== "cancelled")
            .map((task) => (
              <li
                key={task.id}
                className="fc-uploads__row"
                data-status={task.status}
              >
                <div className="fc-uploads__row-head">
                  <span className="fc-uploads__name" title={task.file.name}>
                    {task.file.name}
                  </span>
                  <span className="fc-uploads__stage">
                    {t(STAGE_LABEL[task.status])}
                  </span>
                </div>

                {/*
                  Said plainly, once, on the file it applies to.

                  A group where some photographs are end-to-end encrypted and
                  others are not is a fact worth one line — and the person who
                  can act on it is the one standing here, who can re-add the
                  photograph from a phone that opens the format.
                */}
                {task.unsealed && task.status !== "failed" && (
                  <p className="fc-uploads__note">
                    This browser can&rsquo;t open{" "}
                    {task.file.type || "that format"}, so this one isn&rsquo;t
                    end-to-end encrypted.
                  </p>
                )}

                {/* A bar only while there is genuinely something to measure. */}
                {["hashing", "uploading", "finalising"].includes(
                  task.status,
                ) && (
                  <div className="fc-uploads__row-track">
                    <div
                      className="fc-uploads__row-fill"
                      style={{ transform: `scaleX(${task.progress})` }}
                    />
                  </div>
                )}

                {/* Server-side work has no percentage to report, so the bar
                    sweeps instead of pretending to know how far along it is. */}
                {task.status === "processing" && (
                  <div
                    className="fc-uploads__row-track"
                    data-indeterminate="true"
                  >
                    <div className="fc-uploads__row-sweep" />
                  </div>
                )}

                {task.error && (
                  <p className="fc-uploads__error">{task.error}</p>
                )}

                <div className="fc-uploads__row-actions">
                  {task.status === "failed" && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => onRetry(task.id)}
                    >
                      {t("Try again")}
                    </Button>
                  )}
                  {["queued", "hashing", "uploading"].includes(task.status) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onCancel(task.id)}
                    >
                      {t("Cancel")}
                    </Button>
                  )}
                  <span className="fc-uploads__size fc-tabular">
                    {formatBytes(task.file.size)}
                  </span>
                </div>
              </li>
            ))}
        </ul>
      )}

      {settled && (
        <div className="fc-uploads__actions">
          {failedCount > 0 && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                tasks
                  .filter((task) => task.status === "failed")
                  .forEach((task) => onRetry(task.id))
              }
            >
              Retry all
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onDismiss}>
            Dismiss
          </Button>
        </div>
      )}
    </section>
  );
}
